import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Search,
    Filter,
    Loader2,
    CreditCard,
    User,
    Eye,
    Snowflake,
    ShieldOff,
    ShieldCheck,
    XOctagon,
    RefreshCw,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    MessageSquare,
    DollarSign,
    Lock,
    Unlock,
} from 'lucide-react';
import { cardAdminApi, ActiveCard, CardStatus, CardTier } from '../../api/cards.api';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatUSD = (v?: number) =>
    v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '—';

const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const STATUS_COLOR: Record<CardStatus, string> = {
    ACTIVE: 'bg-green-50 text-green-700 border-green-200',
    FROZEN: 'bg-blue-50 text-blue-600 border-blue-200',
    BLOCKED: 'bg-red-50 text-red-600 border-red-200',
    CANCELLED: 'bg-slate-100 text-slate-500 border-slate-200',
    INACTIVE: 'bg-amber-50 text-amber-600 border-amber-200',
    EXPIRED: 'bg-slate-50 text-slate-400 border-slate-100',
};

const TIER_COLOR: Record<CardTier, string> = {
    standard: 'bg-slate-100 text-slate-700 border-slate-300',
    premium: 'bg-amber-50 text-amber-700 border-amber-300',
};

const STATUS_FILTERS: { key: CardStatus | 'all'; label: string }[] = [
    { key: 'all', label: 'Tout' },
    { key: 'ACTIVE', label: 'Aktif' },
    { key: 'FROZEN', label: 'Frozen' },
    { key: 'BLOCKED', label: 'Bloke' },
    { key: 'INACTIVE', label: 'Inaktif' },
    { key: 'CANCELLED', label: 'Anile' },
];

type ActionType = 'freeze' | 'unfreeze' | 'block' | 'unblock' | 'cancel';

const ACTION_CONFIG: Record<ActionType, { label: string; color: string; icon: React.ReactNode; requiresNote: boolean }> = {
    freeze: { label: 'Fige Kat', color: 'bg-blue-600 hover:bg-blue-700', icon: <Snowflake className="h-4 w-4" />, requiresNote: false },
    unfreeze: { label: 'Defige Kat', color: 'bg-green-600 hover:bg-green-700', icon: <Unlock className="h-4 w-4" />, requiresNote: false },
    block: { label: 'Bloke Kat', color: 'bg-red-600 hover:bg-red-700', icon: <ShieldOff className="h-4 w-4" />, requiresNote: true },
    unblock: { label: 'Debloke Kat', color: 'bg-green-600 hover:bg-green-700', icon: <ShieldCheck className="h-4 w-4" />, requiresNote: false },
    cancel: { label: 'Anile Kat', color: 'bg-rose-700 hover:bg-rose-800', icon: <XOctagon className="h-4 w-4" />, requiresNote: true },
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CardsManagePage() {
    const { toast } = useToast();

    // List state
    const [cards, setCards] = useState<ActiveCard[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<CardStatus | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const LIMIT = 20;

    // Detail / action state
    const [selected, setSelected] = useState<ActiveCard | null>(null);
    const [actionType, setActionType] = useState<ActionType | null>(null);
    const [actionNote, setActionNote] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // ── Data loading ──────────────────────────────────────────────────────────

    const loadCards = useCallback(async () => {
        try {
            setLoading(true);
            const res = await cardAdminApi.listCards({
                page,
                limit: LIMIT,
                status: statusFilter,
                search: searchTerm || undefined,
            });
            setCards(Array.isArray(res?.results) ? res.results : []);
            setTotal(res?.total ?? 0);
        } catch {
            setCards([]);
            toast({ title: 'Erè', description: 'Pa kapab chaje kat yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter, searchTerm]);

    useEffect(() => { loadCards(); }, [loadCards]);
    useEffect(() => { setPage(1); }, [statusFilter, searchTerm]);

    // ── Actions ───────────────────────────────────────────────────────────────

    const handleAction = async () => {
        if (!selected || !actionType) return;
        const cfg = ACTION_CONFIG[actionType];
        if (cfg.requiresNote && !actionNote.trim()) {
            toast({ title: 'Atansyon', description: 'Ou dwe ekri yon rezon', type: 'error' });
            return;
        }
        try {
            setActionLoading(true);
            if (actionType === 'freeze') await cardAdminApi.freezeCard(selected.id, actionNote || undefined);
            else if (actionType === 'unfreeze') await cardAdminApi.unfreezeCard(selected.id);
            else if (actionType === 'block') await cardAdminApi.blockCard(selected.id, actionNote);
            else if (actionType === 'unblock') await cardAdminApi.unblockCard(selected.id, actionNote || undefined);
            else if (actionType === 'cancel') await cardAdminApi.cancelCard(selected.id, actionNote);

            toast({ title: 'Siksè ✓', description: `Kat ${cfg.label.toLowerCase()} avèk siksè`, type: 'success' });
            setSelected(null);
            setActionType(null);
            setActionNote('');
            loadCards();
        } catch (err: any) {
            toast({
                title: 'Erè',
                description: err.response?.data?.error || 'Yon erè rive pandan tretman an',
                type: 'error',
            });
        } finally {
            setActionLoading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    const getAvailableActions = (card: ActiveCard): ActionType[] => {
        switch (card.status) {
            case 'ACTIVE': return ['freeze', 'block', 'cancel'];
            case 'FROZEN': return ['unfreeze', 'block', 'cancel'];
            case 'BLOCKED': return ['unblock', 'cancel'];
            default: return [];
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">

                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem><BreadcrumbLink>Kat VISA</BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem><BreadcrumbPage>Jere Kat</BreadcrumbPage></BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Jere Kat Aktif</h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Fige, bloke, oswa anile kat VISA kliyan yo.
                            </p>
                        </div>
                        <Button variant="outline" size="sm" className="gap-2 border-slate-200 self-start sm:self-auto" onClick={loadCards}>
                            <RefreshCw className="h-4 w-4" /> Aktyalize
                        </Button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <Card className="border-none shadow-sm bg-gradient-to-br from-primary-600 to-primary-700 text-white">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-primary-100 text-xs font-semibold uppercase">Total Kat</p>
                                <p className="text-3xl font-bold mt-1">{total}</p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-gradient-to-br from-green-500 to-green-600 text-white">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-green-100 text-xs font-semibold uppercase">Aktif</p>
                                <p className="text-3xl font-bold mt-1">{cards.filter(c => c.status === 'ACTIVE').length}</p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">Frozen</p>
                                <p className="text-3xl font-bold mt-1 text-blue-600">{cards.filter(c => c.status === 'FROZEN').length}</p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">Bloke</p>
                                <p className="text-3xl font-bold mt-1 text-red-600">{cards.filter(c => c.status === 'BLOCKED').length}</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Status Tabs */}
                    <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
                        {STATUS_FILTERS.map(({ key, label }) => (
                            <button
                                key={key}
                                onClick={() => setStatusFilter(key as CardStatus | 'all')}
                                className={cn(
                                    'flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                                    statusFilter === key ? 'bg-primary-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'
                                )}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                            <Input
                                placeholder="Chèche pa non titilè, dènye 4 chif..."
                                className="pl-10 h-10 border-slate-200 rounded-xl"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <Button variant="outline" className="gap-2 h-10 px-4 rounded-xl border-slate-200">
                            <Filter className="h-4 w-4" /> Filtè
                        </Button>
                    </div>

                    {/* Table */}
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50 border-b border-slate-200">
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Titilè</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Nimewo</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Solde</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Estati</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Ekspire</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Aksyon</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {loading ? (
                                        <tr><td colSpan={6} className="px-6 py-16 text-center">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                            <p className="text-slate-500 mt-3 text-sm">Ap chaje kat yo...</p>
                                        </td></tr>
                                    ) : cards.length === 0 ? (
                                        <tr><td colSpan={6} className="px-6 py-16 text-center">
                                            <div className="bg-slate-50 rounded-full h-14 w-14 flex items-center justify-center mx-auto">
                                                <CreditCard className="h-7 w-7 text-slate-300" />
                                            </div>
                                            <p className="text-slate-500 mt-3 font-medium">Okenn kat jwenn</p>
                                        </td></tr>
                                    ) : (
                                        cards.map((card) => {
                                            const actions = getAvailableActions(card);
                                            return (
                                                <tr key={card.id} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                                                <User className="h-4 w-4 text-primary-600" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-bold text-slate-900 leading-tight">
                                                                    {card.holder?.fullName ?? 'Titilè'}
                                                                </p>
                                                                <p className="text-xs text-slate-400 mt-0.5">
                                                                    {card.holder?.email ?? card.clientId.slice(0, 16) + '…'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex flex-col gap-1">
                                                            <span className="text-sm font-mono font-bold text-slate-900">
                                                                •••• {card.lastFour ?? '????'}
                                                            </span>
                                                            {card.tier && (
                                                                <Badge variant="outline" className={cn('text-xs w-fit', TIER_COLOR[card.tier])}>
                                                                    {card.tier === 'premium' ? '★ Premium' : 'Standard'}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {card.balance ? (
                                                            <div>
                                                                <p className="text-sm font-bold text-slate-900">
                                                                    {formatUSD(card.balance.availableBalance)}
                                                                </p>
                                                                <p className="text-xs text-slate-400">disponib</p>
                                                            </div>
                                                        ) : (
                                                            <span className="text-slate-400 text-sm">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <Badge
                                                            variant="outline"
                                                            className={cn('text-xs font-semibold', STATUS_COLOR[card.status])}
                                                        >
                                                            {card.status}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">
                                                        {card.expiryDate ?? '—'}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0"
                                                                onClick={() => setSelected(card)}
                                                            >
                                                                <Eye className="h-4 w-4 text-slate-400" />
                                                            </Button>
                                                            {actions.includes('freeze') && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="text-blue-600 border-blue-100 hover:bg-blue-50 h-8 gap-1"
                                                                    onClick={() => { setSelected(card); setActionType('freeze'); }}
                                                                >
                                                                    <Snowflake className="h-3.5 w-3.5" /> Fige
                                                                </Button>
                                                            )}
                                                            {actions.includes('unfreeze') && (
                                                                <Button
                                                                    size="sm"
                                                                    className="bg-green-600 hover:bg-green-700 h-8 gap-1 shadow-sm"
                                                                    onClick={() => { setSelected(card); setActionType('unfreeze'); }}
                                                                >
                                                                    <Unlock className="h-3.5 w-3.5" /> Defige
                                                                </Button>
                                                            )}
                                                            {actions.includes('block') && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="text-red-600 border-red-100 hover:bg-red-50 h-8 gap-1"
                                                                    onClick={() => { setSelected(card); setActionType('block'); }}
                                                                >
                                                                    <Lock className="h-3.5 w-3.5" /> Bloke
                                                                </Button>
                                                            )}
                                                            {actions.includes('unblock') && (
                                                                <Button
                                                                    size="sm"
                                                                    className="bg-green-600 hover:bg-green-700 h-8 gap-1 shadow-sm"
                                                                    onClick={() => { setSelected(card); setActionType('unblock'); }}
                                                                >
                                                                    <ShieldCheck className="h-3.5 w-3.5" /> Debloke
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                                <p className="text-sm text-slate-500">
                                    {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} sou {total} kat
                                </p>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-slate-200" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <span className="text-sm font-medium text-slate-700">{page}</span>
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-slate-200" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            {/* ── Card Detail Modal ─────────────────────────────────────────────────── */}
            <Dialog open={!!selected && !actionType} onOpenChange={(open) => { if (!open) setSelected(null); }}>
                <DialogContent className="sm:max-w-md">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <CreditCard className="h-5 w-5 text-primary-500" />
                                    Kat •••• {selected.lastFour ?? '????'}
                                </DialogTitle>
                                <DialogDescription className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline" className={cn('text-xs font-semibold', STATUS_COLOR[selected.status])}>
                                        {selected.status}
                                    </Badge>
                                    {selected.tier && (
                                        <Badge variant="outline" className={cn('text-xs', TIER_COLOR[selected.tier])}>
                                            {selected.tier === 'premium' ? '★ Premium' : 'Standard'}
                                        </Badge>
                                    )}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-2">
                                <div className="grid grid-cols-2 gap-3">
                                    <InfoRow label="Titilè" value={selected.holder?.fullName} />
                                    <InfoRow label="Imèl" value={selected.holder?.email} />
                                    <InfoRow label="Telefòn" value={selected.holder?.phone} />
                                    <InfoRow label="Produi" value={selected.productName} />
                                    <InfoRow label="Devise" value={selected.currency} />
                                    <InfoRow label="Ekspire" value={selected.expiryDate} />
                                    {selected.balance && (
                                        <>
                                            <InfoRow label="Solde disponib" value={formatUSD(selected.balance.availableBalance)} />
                                            <InfoRow label="Solde kouran" value={formatUSD(selected.balance.currentBalance)} />
                                        </>
                                    )}
                                    <InfoRow label="HyperPay ID" value={selected.hyperPayCardId} mono />
                                    {(selected as any).blockedReason && (
                                        <div className="col-span-2">
                                            <InfoRow label="Rezon blokaj" value={(selected as any).blockedReason} />
                                        </div>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
                                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                        <p className="font-semibold uppercase tracking-wider">Aktive</p>
                                        <p className="font-mono mt-1">{formatDate((selected as any).activatedAt)}</p>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                        <p className="font-semibold uppercase tracking-wider">Kreye</p>
                                        <p className="font-mono mt-1">{formatDate(selected.createdAt)}</p>
                                    </div>
                                </div>

                                {/* Actions */}
                                {getAvailableActions(selected).length > 0 && (
                                    <div className="border-t border-slate-100 pt-4">
                                        <p className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Aksyon</p>
                                        <div className="flex flex-wrap gap-2">
                                            {getAvailableActions(selected).map((act) => {
                                                const cfg = ACTION_CONFIG[act];
                                                return (
                                                    <Button
                                                        key={act}
                                                        size="sm"
                                                        className={cn('gap-1.5 shadow-sm', cfg.color, act === 'cancel' ? 'ml-auto' : '')}
                                                        onClick={() => setActionType(act)}
                                                    >
                                                        {cfg.icon} {cfg.label}
                                                    </Button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Action Confirmation Dialog ────────────────────────────────────────── */}
            <Dialog
                open={!!selected && !!actionType}
                onOpenChange={(open) => { if (!open) { setActionType(null); setActionNote(''); } }}
            >
                <DialogContent className="sm:max-w-md">
                    {selected && actionType && (() => {
                        const cfg = ACTION_CONFIG[actionType];
                        return (
                            <>
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2">
                                        {cfg.icon}
                                        {cfg.label}
                                    </DialogTitle>
                                    <DialogDescription>
                                        Kat •••• <strong>{selected.lastFour ?? '????'}</strong> —{' '}
                                        <strong>{selected.holder?.fullName ?? 'Titilè'}</strong>
                                    </DialogDescription>
                                </DialogHeader>

                                <div className="space-y-4 py-2">
                                    {actionType === 'cancel' && (
                                        <div className="flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-100">
                                            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                            <p className="text-sm text-red-800">
                                                Aksyon sa <strong>definitif</strong>. Kat la pa ka aktive ankò apre anilasyon.
                                            </p>
                                        </div>
                                    )}
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                            <MessageSquare className="h-4 w-4 text-slate-400" />
                                            {cfg.requiresNote ? 'Rezon *' : 'Nòt opsyonèl'}
                                        </label>
                                        <Textarea
                                            placeholder={cfg.requiresNote ? 'Rezon obligatwa...' : 'Nòt si nesesè...'}
                                            className="resize-none min-h-[80px] border-slate-200"
                                            value={actionNote}
                                            onChange={(e) => setActionNote(e.target.value)}
                                        />
                                        {cfg.requiresNote && !actionNote.trim() && (
                                            <p className="text-xs text-red-500 flex items-center gap-1">
                                                <AlertCircle className="h-3 w-3" /> Chan sa obligatwa
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <DialogFooter className="gap-2 sm:gap-0">
                                    <Button variant="outline" className="border-slate-200" onClick={() => { setActionType(null); setActionNote(''); }}>
                                        Anile
                                    </Button>
                                    <Button
                                        className={cn('gap-2 shadow-md text-white', cfg.color)}
                                        onClick={handleAction}
                                        disabled={actionLoading || (cfg.requiresNote && !actionNote.trim())}
                                    >
                                        {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : cfg.icon}
                                        Konfime
                                    </Button>
                                </DialogFooter>
                            </>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ─── Helper component ─────────────────────────────────────────────────────────
function InfoRow({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
    return (
        <div>
            <p className="text-xs text-slate-400">{label}</p>
            <p className={cn('text-sm font-semibold text-slate-800 mt-0.5', mono ? 'font-mono text-xs' : '')}>
                {value ?? '—'}
            </p>
        </div>
    );
}
